// src/pages/Shop.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { openRazorpayCheckout } from '../services/razorpay';

// ── Delivery Rules ─────────────────────────────────────────────
const DELIVERY_CHARGE    = 49;
const FREE_DELIVERY_MIN  = 499;   // ₹499+ = Free Delivery
const FREE_HEALTH_CHECK  = 999;   // ₹999+ = Free Delivery + Free Plant Health Check

const getDeliveryBenefit = (total) => {
  if (total >= FREE_HEALTH_CHECK) return { charge: 0, badge: '🎉 Free Delivery + Free Plant Health Check!', color: 'bg-green-100 text-[#2e7d32] border-green-300' };
  if (total >= FREE_DELIVERY_MIN) return { charge: 0, badge: '✅ Free Delivery!', color: 'bg-green-50 text-[#2e7d32] border-green-200' };
  const needed = FREE_DELIVERY_MIN - total;
  return { charge: DELIVERY_CHARGE, badge: `Add ₹${needed} more for FREE delivery`, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
};

const CATEGORIES = ['All', 'Plants', 'Pots & Planters', 'Fertilizers', 'Cocopeat & Soil', 'Pest Control'];

export default function Shop() {
  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [cart,       setCart]       = useState([]);
  const [filter,     setFilter]     = useState('All');
  const [search,     setSearch]     = useState('');
  const [added,      setAdded]      = useState(null);
  const [cartOpen,   setCartOpen]   = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [payError,   setPayError]   = useState('');
  const [orderDone,  setOrderDone]  = useState(false);
  const [orderId,    setOrderId]    = useState('');
  const [buyerInfo,  setBuyerInfo]  = useState({ name: '', phone: '', email: '', address: '' });
  const [showBuyer,  setShowBuyer]  = useState(false);
  const [buyerErr,   setBuyerErr]   = useState('');

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('products').select('*').eq('in_stock', true).order('category', { ascending: true });
      if (error) throw error;
      setProducts(data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const cartCount    = cart.reduce((s, i) => s + i.qty, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const delivery     = getDeliveryBenefit(cartSubtotal);
  const cartTotal    = cartSubtotal + delivery.charge;

  const addToCart = plant => {
    setCart(prev => {
      const ex = prev.find(i => i.id === plant.id);
      if (ex) return prev.map(i => i.id === plant.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...plant, qty: 1 }];
    });
    setAdded(plant.id);
    setTimeout(() => setAdded(null), 1200);
  };

  const removeFromCart = id => setCart(prev => prev.filter(i => i.id !== id));
  const updateQty = (id, d) => setCart(prev => prev.map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + d) } : i));

  const filtered = products.filter(p => {
    const matchCat    = filter === 'All' || p.category === filter;
    const matchSearch = search === '' ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.tag || '').toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  // Group by category for display
  const grouped = CATEGORIES.filter(c => c !== 'All').reduce((acc, cat) => {
    const items = filtered.filter(p => p.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const handleCheckout = () => { setShowBuyer(true); setPayError(''); setBuyerErr(''); };

  const handlePay = async () => {
    if (!buyerInfo.name.trim())            { setBuyerErr('Please enter your name.'); return; }
    if (!/^\d{10}$/.test(buyerInfo.phone)) { setBuyerErr('Please enter a valid 10-digit phone number.'); return; }

    setPayLoading(true); setPayError(''); setBuyerErr('');
    try {
      await openRazorpayCheckout({
        amount:      cartTotal,
        description: `BalconyGreen Order — ${cartCount} item(s)`,
        receipt:     `shop_${Date.now()}`.slice(0, 40),
        notes: {
          type:     'shop',
          items:    cart.map(i => `${i.name} x${i.qty}`).join(', '),
          customer: buyerInfo.name,
          phone:    buyerInfo.phone,
        },
        prefill: { name: buyerInfo.name, contact: buyerInfo.phone, email: buyerInfo.email },
        onSuccess: async (response, order) => {
          const orderItems = cart.map(i => ({
            product_id: i.id, product_name: i.name,
            price: i.price, qty: i.qty, subtotal: i.price * i.qty,
          }));

          const { data: savedOrder } = await supabase.from('orders').insert([{
            customer_name:    buyerInfo.name.trim(),
            customer_phone:   buyerInfo.phone,
            customer_email:   buyerInfo.email.trim(),
            items:            orderItems,
            total_amount:     cartTotal,
            payment_id:       response.razorpay_payment_id,
            payment_status:   'paid',
            order_status:     'Pending',
            delivery_address: buyerInfo.address.trim(),
            notes:            delivery.charge === 0 && cartSubtotal >= FREE_HEALTH_CHECK
                                ? 'Free Plant Health Check included'
                                : delivery.charge === 0 ? 'Free Delivery' : `Delivery: ₹${DELIVERY_CHARGE}`,
          }]).select();

          await supabase.from('payments').insert([{
            order_id: order.id, payment_id: response.razorpay_payment_id,
            amount: cartTotal * 100, currency: 'INR', status: 'paid', type: 'shop',
            notes: { items: cart.map(i => `${i.name} x${i.qty}`).join(', '), customer: buyerInfo.name, phone: buyerInfo.phone, total: cartTotal },
          }]);

          if (buyerInfo.email) {
            await supabase.functions.invoke('send-email', {
              body: {
                type: 'order_confirmation', to: buyerInfo.email.trim(),
                data: {
                  name: buyerInfo.name.trim(), phone: buyerInfo.phone,
                  items: orderItems, subtotal: cartSubtotal,
                  delivery_charge: delivery.charge, total: cartTotal,
                  health_check: cartSubtotal >= FREE_HEALTH_CHECK,
                  payment_id: response.razorpay_payment_id,
                  order_id: savedOrder?.[0]?.id?.slice(0,8).toUpperCase() || '',
                },
              },
            });
            await supabase.functions.invoke('send-email', {
              body: {
                type: 'order_admin', to: 'support@balconygreen.in',
                data: {
                  name: buyerInfo.name.trim(), phone: buyerInfo.phone,
                  email: buyerInfo.email.trim(), address: buyerInfo.address.trim(),
                  items: orderItems, subtotal: cartSubtotal,
                  delivery_charge: delivery.charge, total: cartTotal,
                  health_check: cartSubtotal >= FREE_HEALTH_CHECK,
                  payment_id: response.razorpay_payment_id,
                },
              },
            });
          }

          setOrderId(savedOrder?.[0]?.id?.slice(0,8).toUpperCase() || 'ORD001');
          setCart([]); setOrderDone(true); setShowBuyer(false); setPayLoading(false);
        },
        onFailure: (msg) => {
          if (msg !== 'Payment cancelled by user.') setPayError(`Payment failed: ${msg}`);
          setPayLoading(false);
        },
      });
    } catch (err) { setPayError(`Payment error: ${err.message}`); setPayLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-green-50 border-b border-green-100 text-center py-2 text-sm text-gray-600 mt-16">
        Serving <span className="font-semibold text-gray-800">Ulwe, Navi Mumbai - 410206</span>
      </div>

      {/* Delivery Banner */}
      <div className="bg-[#2e7d32] text-white py-2.5 px-4 text-center text-sm font-medium">
        🚚 Order ₹499+ → <strong>Free Delivery</strong> &nbsp;|&nbsp; 🌿 Order ₹999+ → <strong>Free Delivery + Free Plant Health Check!</strong>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">Shop</h1>
            <p className="text-gray-500 text-sm">Plants, Pots, Fertilizers & more — delivered to your door</p>
          </div>
          <button onClick={() => { setCartOpen(true); setOrderDone(false); setShowBuyer(false); setPayError(''); }}
            className="relative flex items-center gap-2 px-4 py-2.5 bg-[#2e7d32] text-white text-sm font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            Cart
            {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-white text-[#2e7d32] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border border-[#2e7d32]">{cartCount}</span>}
          </button>
        </div>

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search plants, pots, fertilizers..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32] bg-white" />
            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg">×</button>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setFilter(cat)}
                className={`px-3 py-2.5 rounded-xl text-xs font-medium transition-all whitespace-nowrap
                  ${filter === cat ? 'bg-[#2e7d32] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#2e7d32] hover:text-[#2e7d32]'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-[#2e7d32] border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🌱</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">{search ? `No results for "${search}"` : 'No products available'}</h3>
            {search && <button onClick={() => setSearch('')} className="mt-2 px-4 py-2 bg-[#2e7d32] text-white text-sm font-semibold rounded-xl">Clear Search</button>}
          </div>
        ) : filter !== 'All' ? (
          // Single category view
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filtered.map(product => <ProductCard key={product.id} product={product} added={added} addToCart={addToCart} />)}
          </div>
        ) : (
          // Grouped by category
          <div className="space-y-10">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {getCategoryEmoji(cat)} {cat}
                    <span className="ml-2 text-sm text-gray-400 font-normal">({items.length})</span>
                  </h2>
                  <button onClick={() => setFilter(cat)} className="text-sm text-[#2e7d32] hover:underline">View all →</button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {items.slice(0, 4).map(product => <ProductCard key={product.id} product={product} added={added} addToCart={addToCart} />)}
                </div>
                {items.length > 4 && (
                  <button onClick={() => setFilter(cat)} className="mt-4 w-full py-2.5 border-2 border-dashed border-gray-200 text-gray-500 text-sm rounded-xl hover:border-[#2e7d32] hover:text-[#2e7d32] transition-colors">
                    +{items.length - 4} more in {cat}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cart Sidebar */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setCartOpen(false); setShowBuyer(false); setPayError(''); }} />
          <aside className="relative bg-white w-full max-w-sm shadow-2xl flex flex-col h-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-lg text-gray-900">{orderDone ? '🎉 Order Placed!' : showBuyer ? 'Delivery Details' : `Cart (${cartCount})`}</h2>
              <button onClick={() => { setCartOpen(false); setShowBuyer(false); setPayError(''); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            {/* Order success */}
            {orderDone ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Payment Successful! 🎉</h3>
                <div className="bg-green-50 rounded-xl px-4 py-2 mb-3">
                  <p className="text-xs text-gray-500">Order ID</p>
                  <p className="font-bold text-[#2e7d32] text-lg">#{orderId}</p>
                </div>
                {cartSubtotal >= FREE_HEALTH_CHECK && (
                  <div className="bg-green-100 border border-green-300 rounded-xl px-4 py-2 mb-3 w-full">
                    <p className="text-sm font-semibold text-[#2e7d32]">🌿 Free Plant Health Check Included!</p>
                    <p className="text-xs text-gray-500">Our expert will check your plants on delivery</p>
                  </div>
                )}
                <p className="text-sm text-gray-500 mb-6">We'll contact you at <strong>{buyerInfo.phone}</strong> to arrange delivery.</p>
                <button onClick={() => { setCartOpen(false); setOrderDone(false); }} className="px-6 py-2.5 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors text-sm">Continue Shopping</button>
              </div>

            /* Buyer info */
            ) : showBuyer ? (
              <div className="flex-1 flex flex-col p-5 overflow-y-auto">
                <p className="text-sm text-gray-500 mb-4">Enter your delivery details to proceed.</p>
                {buyerErr && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">{buyerErr}</div>}
                {payError && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">{payError}</div>}
                <div className="space-y-3 mb-4">
                  {[
                    { label: 'Full Name *', key: 'name', type: 'text', placeholder: 'Your name' },
                    { label: 'Phone Number *', key: 'phone', type: 'tel', placeholder: '10-digit mobile', maxLen: 10 },
                    { label: 'Email (for confirmation)', key: 'email', type: 'email', placeholder: 'your@email.com' },
                  ].map(({ label, key, type, placeholder, maxLen }) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                      <input type={type} value={buyerInfo[key]} maxLength={maxLen}
                        onChange={e => { setBuyerInfo(b => ({ ...b, [key]: e.target.value })); setBuyerErr(''); }}
                        placeholder={placeholder}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Delivery Address</label>
                    <textarea value={buyerInfo.address} rows={2}
                      onChange={e => setBuyerInfo(b => ({ ...b, address: e.target.value }))}
                      placeholder="Flat no, Building, Area, Ulwe..."
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32] resize-none" />
                  </div>
                </div>

                {/* Order summary */}
                <div className="bg-gray-50 rounded-xl p-3 mb-4">
                  <p className="text-xs text-gray-500 mb-2 font-medium">Order Summary</p>
                  {cart.map(item => (
                    <div key={item.id} className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>{item.name} × {item.qty}</span><span>₹{item.price * item.qty}</span>
                    </div>
                  ))}
                  <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Subtotal</span><span>₹{cartSubtotal}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className={delivery.charge === 0 ? 'text-[#2e7d32] font-medium' : 'text-gray-500'}>
                        Delivery {delivery.charge === 0 ? '🎉 FREE' : ''}
                      </span>
                      <span className={delivery.charge === 0 ? 'text-[#2e7d32] font-medium' : 'text-gray-600'}>
                        {delivery.charge === 0 ? 'FREE' : `₹${delivery.charge}`}
                      </span>
                    </div>
                    {cartSubtotal >= FREE_HEALTH_CHECK && (
                      <div className="flex justify-between text-xs text-[#2e7d32] font-medium">
                        <span>🌿 Plant Health Check</span><span>FREE</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-bold border-t border-gray-200 pt-1 mt-1">
                      <span>Total</span><span className="text-[#2e7d32]">₹{cartTotal}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <button onClick={handlePay} disabled={payLoading}
                    className="w-full py-3 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors disabled:opacity-60 text-sm">
                    {payLoading ? 'Opening Payment...' : `💳 Pay ₹${cartTotal}`}
                  </button>
                  <button onClick={() => { setShowBuyer(false); setPayError(''); setBuyerErr(''); }}
                    className="w-full py-2.5 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm">
                    ← Back to Cart
                  </button>
                </div>
              </div>

            /* Cart items */
            ) : cart.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="text-5xl mb-3">🛒</div>
                <p className="text-gray-500 text-sm">Your cart is empty!</p>
                <p className="text-xs text-gray-400 mt-1">Add plants, pots or fertilizers</p>
              </div>
            ) : (
              <>
                <ul className="flex-1 overflow-y-auto divide-y divide-gray-50 px-4 py-2">
                  {cart.map(item => (
                    <li key={item.id} className="flex items-center gap-3 py-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-green-50 flex-shrink-0">
                        {item.image_url ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xl">{getCategoryEmoji(item.category)}</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-[#2e7d32] text-sm font-semibold">₹{item.price}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(item.id, -1)} className="w-6 h-6 rounded-full bg-gray-100 text-sm flex items-center justify-center hover:bg-gray-200">−</button>
                        <span className="w-6 text-center text-sm font-medium">{item.qty}</span>
                        <button onClick={() => updateQty(item.id, +1)} className="w-6 h-6 rounded-full bg-gray-100 text-sm flex items-center justify-center hover:bg-gray-200">+</button>
                      </div>
                      <button onClick={() => removeFromCart(item.id)} className="text-red-400 hover:text-red-600 ml-1">✕</button>
                    </li>
                  ))}
                </ul>

                {/* Delivery benefit banner */}
                <div className={`mx-4 mb-2 px-3 py-2 rounded-xl border text-xs font-medium ${delivery.color}`}>
                  {delivery.badge}
                  {delivery.charge > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5">
                      <div className="bg-[#2e7d32] h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((cartSubtotal / FREE_DELIVERY_MIN) * 100, 100)}%` }} />
                    </div>
                  )}
                </div>

                <div className="px-6 py-5 border-t border-gray-100">
                  <div className="space-y-1 mb-4">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Subtotal ({cartCount} items)</span><span>₹{cartSubtotal}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Delivery</span>
                      <span className={delivery.charge === 0 ? 'text-[#2e7d32] font-semibold' : 'text-gray-700'}>
                        {delivery.charge === 0 ? 'FREE 🎉' : `₹${delivery.charge}`}
                      </span>
                    </div>
                    <div className="flex justify-between font-bold text-base border-t border-gray-100 pt-2 mt-1">
                      <span>Total</span><span className="text-[#2e7d32]">₹{cartTotal}</span>
                    </div>
                  </div>
                  <button onClick={handleCheckout} className="w-full py-3 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors">
                    Proceed to Checkout →
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

// ── Product Card Component ─────────────────────────────────────
function ProductCard({ product, added, addToCart }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
      <div className="h-48 overflow-hidden bg-green-50">
        {product.image_url
          ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              onError={e => { e.target.style.display='none'; e.target.parentElement.innerHTML=`<div class="w-full h-full flex items-center justify-center text-5xl">${getCategoryEmoji(product.category)}</div>`; }} />
          : <div className="w-full h-full flex items-center justify-center text-5xl">{getCategoryEmoji(product.category)}</div>
        }
      </div>
      <div className="p-4">
        {product.tag && <span className="inline-block px-2 py-0.5 bg-green-100 text-[#2e7d32] text-xs font-semibold rounded-full mb-2">{product.tag}</span>}
        <h3 className="font-bold text-gray-900 mb-0.5 text-sm">{product.name}</h3>
        <p className="text-xs text-gray-400 mb-3">{product.category}</p>
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-[#2e7d32]">₹{product.price}</span>
          <button onClick={() => addToCart(product)}
            className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-all duration-200
              ${added === product.id ? 'bg-green-100 text-[#2e7d32]' : 'bg-[#2e7d32] text-white hover:bg-[#1b5e20]'}`}>
            {added === product.id ? '✓ Added' : '+ Cart'}
          </button>
        </div>
      </div>
    </div>
  );
}

function getCategoryEmoji(category) {
  const map = {
    'Plants':           '🌿',
    'Pots & Planters':  '🪴',
    'Fertilizers':      '🌱',
    'Cocopeat & Soil':  '🥥',
    'Pest Control':     '🐛',
  };
  return map[category] || '🌿';
}
